import { isValidObject } from './../../utils/object'
import { Country, CheckoutMode } from '@afterpaytouch/portal-api/types'
import { app } from '../../env'
import { BiometricLoginStatus, APT_USER_ID, UserNameType, FacilityCohort } from '../../model'
import {
  useActiveExperiments,
  useBiometric,
  useCheckoutState,
  useConsumerSelector,
  useCountry,
  useHasBillingAgreements,
  useInitialData,
  useIsBillingAgreementApprovalProcess,
  useIsExpress,
  useIsPrivateBrowsingMode,
  useIsTrustedCheckout,
  useMobileLogin,
  usePaymentInterval,
  useSelectedPaymentOption,
  useSelectedPaymentType,
  useFingerprint,
  useConvergenceTrackingProps,
  useConsumerLendingEligible,
  useHasViewedZeroAprOffer,
  useIsSupOrder,
  useIsPaymonthlySUPEnabled,
  useConsumerFacilityCohort,
  useAppleRetailTrackingProps,
  useCheckoutDarkMode,
  usePaymentTypeAllowInConvergence,
  useIsAppleRetailOrder,
} from '../../state'
import { Money, PaymentType } from '@afterpay/types'
import { getCurrentUserContextFromClient, useLDClient } from '@afterpaytouch/feature'
import { inAppBrowserVersion, isAfterpayInAppBrowser, isCashAppInAppBrowser, isInAppBrowser } from '../../utils/device'
import { parseCookies } from 'nookies'
import { useIsDarkMode, DarkMode } from '@afterpaytouch/core'

interface TrackingEventProperty {
  [key: string]: any
}

interface AmplitudeCustomUserProperties extends TrackingEventProperty {
  country: Country
  consumerId?: number
  activeExperiment: string
  // hasSetupApplePay: boolean // TODO: change to enum
}

interface AmplitudeEnduringEventProperties extends TrackingEventProperty {
  activeExperiment: string
  category: string
  checkoutMode?: CheckoutMode | null
  checkoutRedesign: boolean
  merchantCategoryCode?: number
  merchantCategoryName?: string
  merchantId?: number
  merchantIndustryName?: string
  merchantName?: string
  orderAmount?: Money
  transactionToken: string
  windowType: string
  appEnvironment: string
  appVersion: string
  isBuyNow: boolean
  isPickup: boolean
  isMcp: boolean
  preorder?: boolean
  consumerPendingAmount?: string
  protocol?: string
  zoidVersion: string
  isOAuthTrustedCheckout: boolean
  isBillingAgreementApproval: boolean
  hasBillingAgreement: boolean
  paymentType: PaymentType
  facilityCohort?: FacilityCohort
  isPayMonthlyEligible: boolean
}

export function useUserProperties(): AmplitudeCustomUserProperties {
  const { id, accountCountry, emailLookupCountry } = useConsumerSelector()
  const country = useCountry()
  const activeExperiment = useActiveExperiments()
  return {
    country: accountCountry ?? emailLookupCountry ?? country,
    consumerId: id,
    activeExperiment,
  }
}

export function useEnduringEventProperties(): AmplitudeEnduringEventProperties {
  const { checkout, initial, started } = useCheckoutState()
  const activeExperiment = useActiveExperiments()
  const isTrustedCheckout = useIsTrustedCheckout()
  const isExpress = useIsExpress()
  const { isMobileLogin } = useMobileLogin()
  const { APP_ENVIRONMENT, APP_VERSION } = app
  const hasBillingAgreement = useHasBillingAgreements()
  const isBillingAgreementApproval = useIsBillingAgreementApprovalProcess()
  const { visitorId } = useInitialData()
  const paymentType = useSelectedPaymentType() ?? PaymentType.PBI
  const LDClient = useLDClient()
  const { hasFeatureFlagsReady } = useConsumerSelector()
  const { loginStatus: biometricLoginStatus, isBiometricEnabledOnServer } = useBiometric()
  const inAppBrowser = isInAppBrowser()
  const isPrivateBrowsingMode = useIsPrivateBrowsingMode()
  const fingerprint = useFingerprint()
  const convergenceTrackingProps = useConvergenceTrackingProps()

  const paymentIntervalPBI = usePaymentInterval()
  const paymentOption = useSelectedPaymentOption()
  const paymentInterval = paymentType === PaymentType.PCL && paymentOption !== undefined ? paymentOption.paymentInterval : paymentIntervalPBI
  const cookies = parseCookies()
  const isUserIdCookiePresent = !!cookies[APT_USER_ID]
  const isSingleUsePayment = useIsSupOrder()
  const iabVersion = inAppBrowserVersion()
  const isConsumerLendingEligible = useConsumerLendingEligible()
  const isConsumerLendingAllowInConvergence = usePaymentTypeAllowInConvergence(PaymentType.PCL)
  const isAppleRetailOrder = useIsAppleRetailOrder()
  const hasViewedZeroAprOffer = useHasViewedZeroAprOffer()
  const isPaymonthlySUPEnabled = useIsPaymonthlySUPEnabled()
  const consumerFacilityCohort = useConsumerFacilityCohort()
  const isDivertedToGetStarted = consumerFacilityCohort?.eligibility === 'eligible'
  const isPayMonthlyEligible = isConsumerLendingEligible && isConsumerLendingAllowInConvergence && !isAppleRetailOrder && !isDivertedToGetStarted
  const cohort = consumerFacilityCohort?.cohort
  const appleRetailTrackingProps = useAppleRetailTrackingProps()

  const isDarkMode = useIsDarkMode()
  const [darkMode] = useCheckoutDarkMode()
  // Resolve OS dark-mode preference synchronously. We deliberately do NOT use core's
  // useIsOsDarkMode() here: it reads ThemeContext.isDarkModeActive, which is false on
  // first render until useMediaQuery's effect resolves prefers-color-scheme. Mount /
  // page-view events fire on that first render, so a dark-OS user's first event would
  // log isOsDarkMode=false and skew the very analysis this property exists for. Mirror
  // isDarkModeActive's logic against a synchronous matchMedia read instead (override-aware:
  // an explicit Light/Dark preference wins; System falls back to the OS media query).
  // Wrapped in a function so matchMedia is only evaluated when needed (System case).
  const getOsPrefersDark = (): boolean =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-color-scheme: dark)').matches : false
  const isOsDarkMode = darkMode === DarkMode.Dark ? true : darkMode === DarkMode.Light ? false : getOsPrefersDark()
  return {
    activeExperiment,
    appEnvironment: APP_ENVIRONMENT,
    appVersion: APP_VERSION,
    category: 'Checkout',
    checkoutMode: checkout?.checkoutMode,
    checkoutRedesign: true,
    ...(isExpress && { addressMode: initial.addressMode }),
    ...(biometricLoginStatus === BiometricLoginStatus.Finished && { isBiometricLogin: true }),
    isCrossRegion: isValidObject(checkout?.crossRegionUserSignIn),
    isCashApp: checkout?.cashApp != null,
    preorder: started?.preorder,
    consumerPendingAmount: started?.consumerPendingAmount?.amount,
    merchantAmount: started?.merchantAmount.amount,
    merchantCurrency: checkout?.merchant.merchantCurrencyCode ?? started?.merchantAmount.currency,
    merchantCategoryCode: checkout?.merchant.merchantCategoryCode,
    merchantCategoryName: checkout?.merchant.merchantCategoryName,
    merchantId: checkout?.merchant.id,
    merchantIndustryName: checkout?.merchant.merchantIndustryName,
    merchantName: checkout?.merchant.name,
    orderAmount: started?.merchantAmount ?? checkout?.amount,
    orderCurrency: started?.consumerAmount.currency,
    orderSource: checkout?.merchant.source,
    paymentType,
    transactionToken: initial.token,
    windowType: initial.isIframe ? 'iframe' : initial.isWindowed ? 'popup' : 'redirect',
    isInAppBrowser: inAppBrowser,
    iabVersion: inAppBrowser ? iabVersion : null,
    isSingleUsePayment,
    userAgent: navigator.userAgent,
    language: navigator.language,
    languages: navigator.languages,
    fingerprint: fingerprint?.fingerprint,
    isMcp: checkout?.merchant.merchantCurrencyCode ? checkout?.merchant.merchantCurrencyCode !== checkout?.amount.currency : false,
    isBuyNow: initial.buyNow,
    isPickup: initial.pickup,
    protocol: initial.protocol,
    zoidVersion: initial.zoid,
    ...checkout?.trackingTags,
    isOAuthTrustedCheckout: isTrustedCheckout,
    loginType: isMobileLogin ? UserNameType.Mobile : UserNameType.Email,
    numberOfPaymentInstalments: started?.paymentSchedules.length,
    paymentInterval,
    isBillingAgreementApproval,
    hasBillingAgreement,
    isAfterpayInAppSession: isAfterpayInAppBrowser(),
    isCashAppInAppSession: isCashAppInAppBrowser(),
    ldClient: getCurrentUserContextFromClient(LDClient)?.key,
    isFeatureFlagsReady: hasFeatureFlagsReady,
    isUserIdCookiePresent,
    pageName: document.title,
    isPaymonthlySUPEnabled,
    isDarkMode,
    isOsDarkMode,
    appearance: darkMode,
    ...(cohort !== undefined && { facilityCohort: cohort }),
    ...(visitorId && { aptVisitorId: visitorId }),
    ...(isPrivateBrowsingMode && { isPrivateBrowsingMode }),
    ...(isBiometricEnabledOnServer !== undefined && { isBiometricEnabledOnServer }),
    ...convergenceTrackingProps,
    isPayMonthlyEligible,
    ...(isConsumerLendingEligible && { viewed0AprOffer: hasViewedZeroAprOffer }),
    ...(paymentOption != null && paymentType === PaymentType.PCL && { paymentOption }),
    ...appleRetailTrackingProps,
  }
}
